import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { OrgInstitutionProvider } from './contexts/OrgInstitutionContext';
import NotificationModal from './components/NotificationModal';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import Dashboard from './components/Dashboard';
import Students from './components/Students';
import StudentDetail from './components/StudentDetail';
import Classes from './components/Classes';
import ClassWork from './components/ClassWork';
import ClassFeed from './components/ClassFeed';
import StudentClassFeed from './components/StudentClassFeed';
import Settings from './components/Settings';
import UserManagement from './components/UserManagement';
import Organizations from './components/Organizations';
import Institutions from './components/Institutions';
import ImpersonationBanner from './components/ImpersonationBanner';
import SessionTimeoutWarning from './components/SessionTimeoutWarning';
import DemoLauncher from './components/DemoLauncher';
import StudentLoginPage from './pages/StudentLoginPage';
import StudentDashboardPage from './pages/StudentDashboardPage';
import StudentLayout from './components/StudentLayout';
import StudentClasses from './components/StudentClasses';
import StudentAssignments from './components/StudentAssignments';
import DiagnosticAssessment from './components/DiagnosticAssessment';
import ReadingLevelTest from './components/ReadingLevelTest';
import StudentQuizTaking from './components/StudentQuizTaking';
import StudentQuizResults from './components/StudentQuizResults';
import InteractiveAssignment from './components/InteractiveAssignment';

import './App.css';

// Component to handle OAuth callback parameters
const OAuthCallbackHandler: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [notification, setNotification] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({ isOpen: false, title: '', message: '', type: 'info' });

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    
    // Check for Google Classroom OAuth success
    if (urlParams.get('google_classroom_connected') === 'true') {
      console.log('Google Classroom connected successfully!');
      
      // Show success notification
      setNotification({
        isOpen: true,
        title: 'Google Classroom Connected!',
        message: '🎉 Google Classroom connected successfully! You can now import your classes and students.',
        type: 'success'
      });
      
      // Clean up URL parameters
      const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
      window.history.replaceState({}, document.title, newUrl);
      
      // Navigate to classes page to encourage immediate use
      navigate('/classes');
    }
    
    // Check for Google Classroom OAuth error
    const errorParam = urlParams.get('google_classroom_error');
    if (errorParam) {
      console.error('Google Classroom connection error:', errorParam);
      
      // Show error notification
      setNotification({
        isOpen: true,
        title: 'Connection Failed',
        message: `❌ Google Classroom connection failed: ${decodeURIComponent(errorParam)}\n\nPlease try again or contact support if the issue persists.`,
        type: 'error'
      });
      
      // Clean up URL parameters
      const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [location.search, navigate]);

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }));
  };

  return (
    <NotificationModal
      isOpen={notification.isOpen}
      title={notification.title}
      message={notification.message}
      type={notification.type}
      onClose={handleCloseNotification}
    />
  );
};

function AppRoutes() {
  return (
    <Routes>
      {/* Student Routes */}
      <Route path="/student/login" element={<StudentLoginPage />} />
      <Route 
        path="/student/assessment" 
        element={
          <ProtectedRoute>
            <DiagnosticAssessment />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/student/test" 
        element={
          <ProtectedRoute>
            <ReadingLevelTest />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/student" 
        element={
          <ProtectedRoute>
            <StudentLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/student/dashboard" replace />} />
        <Route path="dashboard" element={<StudentDashboardPage />} />
        <Route path="classes" element={<StudentClasses />} />
        <Route path="classes/:classId/feed" element={<StudentClassFeed />} />
        <Route path="assignments" element={<StudentAssignments />} />
        <Route path="assignments/:assignmentId/interactive" element={<InteractiveAssignment />} />
        <Route path="quiz/:quizAssignmentId" element={<StudentQuizTaking />} />
        <Route path="quiz/:quizAssignmentId/results/:attemptId" element={<StudentQuizResults />} />
      </Route>

      {/* Teacher/Admin Routes */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/students" 
        element={
          <ProtectedRoute>
            <Students />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/students/:id" 
        element={
          <ProtectedRoute>
            <StudentDetail />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/classes" 
        element={
          <ProtectedRoute>
            <Classes />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/classes/:classId/feed" 
        element={
          <ProtectedRoute>
            <ClassFeed />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/class-work" 
        element={
          <ProtectedRoute>
            <ClassWork />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/users" 
        element={
          <AdminRoute>
            <UserManagement />
          </AdminRoute>
        } 
      />
      <Route 
        path="/admin/organizations" 
        element={
          <AdminRoute>
            <Organizations />
          </AdminRoute>
        } 
      />
      <Route 
        path="/admin/institutions" 
        element={
          <AdminRoute>
            <Institutions />
          </AdminRoute>
        } 
      />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <OrgInstitutionProvider>
        <div className="App">
          <Router>
            <ImpersonationBanner />
            <SessionTimeoutWarning />
            <OAuthCallbackHandler />
            <DemoLauncher />
            <AppRoutes />
          </Router>
        </div>
      </OrgInstitutionProvider>
    </AuthProvider>
  );
}

export default App;
